import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Settings2 } from 'lucide-react';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  LEGACY_BOOK_COVER_STYLES,
  LEGACY_BOOK_THEMES,
} from '../../utils/legacyBookPreview';
import { cn } from '@/lib/utils';

const SECTION_LABELS = {
  overview: 'Life overview',
  stories: 'Stories',
  voice: 'Voice memories',
  photos: 'Photos & documents',
  family: 'Family connections',
  memorial: 'Memorial section',
};

export function LegacyBookPreviewToolbar({ options, onChange, className }) {
  const setTheme = (theme) => onChange({ ...options, theme });
  const setCoverStyle = (coverStyle) => onChange({ ...options, coverStyle });
  const toggleSection = (key) => {
    onChange({
      ...options,
      sections: { ...options.sections, [key]: !options.sections[key] },
    });
  };

  return (
    <aside
      className={cn(
        'legacy-book-no-print rounded-2xl border border-[#e8e2d8]/90 bg-white/95 p-4 shadow-sm backdrop-blur-sm sm:p-5',
        className,
      )}
      data-testid="legacy-book-preview-toolbar"
      aria-label="Preview options"
    >
      <div className="flex items-center gap-2 text-[#5c4a32]">
        <Settings2 className="h-4 w-4" aria-hidden />
        <p className="text-xs font-semibold uppercase tracking-[0.14em]">Preview options</p>
      </div>
      <p className="mt-1 text-[11px] leading-relaxed text-[#8b7355]">
        Saved in this browser only. Export-ready design coming next.{' '}
        <Link to="/help#legacy-book" className="underline underline-offset-2 hover:text-[#5c4a32]">
          Legacy Book guide
        </Link>
      </p>

      <div className="mt-4 space-y-4">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Theme</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {LEGACY_BOOK_THEMES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTheme(t.id)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs transition',
                  options.theme === t.id
                    ? 'border-[#c9a961]/50 bg-[#fffaf3] text-[#3d3833]'
                    : 'border-[#e8e2d8] text-muted-foreground hover:border-[#c9a961]/35',
                )}
                data-testid={`legacy-book-theme-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Cover style</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {LEGACY_BOOK_COVER_STYLES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCoverStyle(c.id)}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs transition',
                  options.coverStyle === c.id
                    ? 'border-[#c9a961]/50 bg-[#fffaf3] text-[#3d3833]'
                    : 'border-[#e8e2d8] text-muted-foreground hover:border-[#c9a961]/35',
                )}
                data-testid={`legacy-book-cover-${c.id}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Include sections</Label>
          <ul className="mt-2 space-y-2">
            {Object.entries(SECTION_LABELS).map(([key, label]) => (
              <li key={key} className="flex items-center gap-2">
                <Checkbox
                  id={`lb-section-${key}`}
                  checked={Boolean(options.sections[key])}
                  onCheckedChange={() => toggleSection(key)}
                  data-testid={`legacy-book-section-${key}`}
                />
                <label htmlFor={`lb-section-${key}`} className="text-xs text-[#4a433c] cursor-pointer">
                  {label}
                </label>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-xl border border-[#e8e2d8]/80 bg-[#fffdf8] px-3 py-2.5">
        <BookOpen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#8b7355]" aria-hidden />
        <p className="text-[11px] leading-relaxed text-[#6c6258]">
          This preview shows how the profile could become a polished Legacy Book. Final export styling will continue to
          improve.
        </p>
      </div>
    </aside>
  );
}

export default LegacyBookPreviewToolbar;
